// Content map for SYMPTOM detail pages. Key = URL slug.
//
// Each entry shape:
//  shortTitle, title (H1), subtitle (lede),
//  seoTitle, seoDescription,
//  sections: [{ id, eyebrow, heading, body, bullets?, sidenote? }]  (3 H2 + 2 H3 by DetailTemplate)
//  faq: [{q, a}]
//  related: [{ label, to }]
//  schema: { name, alternateName, signOrSymptom, riskFactor, possibleTreatment }

export const SYMPTOM_CONTENT = {
  "acidity-treatment-varanasi": {
    shortTitle: "Acidity & Heartburn",
    title: "Acidity & Heartburn Treatment in Varanasi",
    subtitle:
      "A burning sensation in the chest, sour reflux into the throat, or worsening symptoms after meals — common, fixable, but worth taking seriously when it stays for weeks.",
    seoTitle:
      "Acidity & Heartburn Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "Best treatment for chronic acidity, heartburn, GERD and reflux in Varanasi. DM Gastroenterologist Dr. Indresh Dixit — Lanka & Chetganj clinics.",
    sections: [
      {
        id: "what",
        eyebrow: "What it feels like",
        heading: "When is it really acidity?",
        body: `Most people use the word "acidity" for a range of upper-GI sensations — burning in the chest after meals (heartburn), sour fluid coming up the throat (reflux), pain in the upper abdomen, bloating after eating, and a chronic dry cough or throat-clearing at night.\n\nIf it lasts more than 3–4 weeks, wakes you up at night, or interferes with sleep and meals, it has stopped being a passing problem and needs evaluation.`,
      },
      {
        id: "causes",
        eyebrow: "Why it happens",
        heading: "The common drivers — in order.",
        bullets: [
          "Weak lower esophageal sphincter (GERD)",
          "Hiatus hernia — stomach pushing into chest",
          "Helicobacter pylori infection",
          "NSAID and pain-killer overuse",
          "Smoking, late dinners, very spicy food",
          "Obesity and central weight gain",
          "Stress and erratic meal timing",
          "Pregnancy (mostly temporary)",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "How we diagnose",
        heading: "Tests that actually matter.",
        body: `For most patients, a careful history is the diagnosis. We add tests only when something does not add up — long duration, alarm symptoms, weight loss, anaemia, difficulty swallowing, or vomiting.\n\nWhen needed, an Upper GI Endoscopy is done in the clinic with mild sedation. It is the single most useful test — it tells us whether there is reflux damage, an ulcer, an infection, or rarely, something more serious.`,
        sidenote:
          "Alarm symptoms that always need endoscopy: difficulty swallowing, weight loss, anaemia, persistent vomiting, blood in vomit, or new symptoms after age 45.",
      },
      {
        id: "treatment",
        eyebrow: "How we treat it",
        heading: "Acid control + the actual cause.",
        body: `Modern acid blockers (PPIs) work — but only as long as you take them. The real fix is treating the underlying reason — H. pylori is cleared with a course of antibiotics, hiatus hernia is managed with positional and lifestyle changes (rarely surgery), and stress/lifestyle patterns are corrected.\n\nWhen done right, most patients are off long-term PPIs in 8–12 weeks.`,
      },
      {
        id: "when",
        eyebrow: "When to see a doctor",
        heading: "Don't wait if you have these.",
        bullets: [
          "Acidity continuing beyond 3–4 weeks",
          "Night-time reflux waking you up",
          "Difficulty or pain on swallowing",
          "Weight loss with acidity",
          "Blood in vomit or black stool",
          "Family history of stomach cancer",
        ],
      },
    ],
    faq: [
      { q: "Is long-term PPI use safe?", a: "PPIs are generally safe but should be used at the lowest effective dose. Long-term use can affect magnesium, B12 and bone density — that is why we structure tapering plans." },
      { q: "Do I need an endoscopy for acidity?", a: "Not always. We recommend endoscopy when symptoms persist >4 weeks, alarm features are present, or after age 45 for a baseline." },
      { q: "Can acidity be permanently cured?", a: "In the majority of patients — yes. Once H. pylori is cleared and triggers are managed, recurrence rates drop significantly." },
      { q: "What foods should I strictly avoid?", a: "Very spicy food, deep-fried items, carbonated drinks, late-night meals (within 2 hours of sleeping), and high-caffeine intake on an empty stomach." },
    ],
    related: [
      { label: "GERD Treatment", to: "/diseases/gerd-treatment" },
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
      { label: "Gas & Bloating", to: "/symptoms/gas-problem-treatment-varanasi" },
    ],
    schema: {
      name: "Acidity (Acid Reflux / Heartburn / GERD)",
      alternateName: ["GERD", "Acid Reflux", "Heartburn"],
      signOrSymptom: ["Burning chest after meals", "Sour reflux", "Pain in upper abdomen", "Bloating after eating", "Chronic night cough"],
      riskFactor: ["Weak LES", "Hiatus hernia", "H. pylori", "NSAID use", "Smoking", "Late dinners", "Obesity"],
      possibleTreatment: ["PPI therapy", "H. pylori eradication", "Lifestyle modification", "Upper GI Endoscopy"],
    },
  },

  "gas-problem-treatment-varanasi": {
    shortTitle: "Gas & Bloating",
    title: "Gas & Bloating Treatment in Varanasi",
    subtitle:
      "Persistent gas, abdominal distension, embarrassing belching or flatulence after meals — there is almost always a cause, and almost always a fix.",
    seoTitle: "Gas & Bloating Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "Treatment for chronic gas, abdominal bloating and excess flatulence in Varanasi. Comprehensive evaluation with Dr. Indresh Dixit — Lanka & Chetganj clinics.",
    sections: [
      {
        id: "what",
        eyebrow: "Understanding the symptom",
        heading: "Gas, bloating — and what's actually wrong.",
        body: `Excessive gas is a daily reality for many patients but rarely a disease in itself. It usually points to something specific — food intolerance, motility issues, an imbalance in gut bacteria, or, less commonly, a more serious condition.\n\nA clear diagnosis prevents months of unnecessary diet restriction and over-the-counter remedies.`,
      },
      {
        id: "causes",
        eyebrow: "Why it happens",
        heading: "What actually causes excess gas.",
        bullets: [
          "Lactose or fructose intolerance",
          "Small Intestinal Bacterial Overgrowth (SIBO)",
          "Irritable Bowel Syndrome (IBS)",
          "Constipation with fermentation",
          "High-FODMAP foods",
          "Swallowed air (rapid eating, carbonated drinks)",
          "Celiac disease",
          "Pancreatic enzyme deficiency",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "How we diagnose",
        heading: "Right tests, in the right order.",
        body: `History matters most — when does it happen, what triggers it, is there pain or diarrhoea, weight loss or a family history of bowel disease.\n\nDepending on the picture, we may add hydrogen-methane breath tests, celiac panel, stool tests, abdominal imaging, or upper/lower endoscopy. We avoid over-investigation.`,
        sidenote:
          "A 2-week structured food diary often reveals the trigger faster than any blood test.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment plan",
        heading: "Targeted, not generic.",
        body: `Treatment is matched to cause — antibiotic + prokinetic for SIBO, low-FODMAP diet for IBS, lactose elimination for intolerance, antispasmodics for cramps, and probiotics where evidence supports it. We avoid blanket prescriptions of "gas tablets" that mask the issue without addressing it.`,
      },
      {
        id: "when",
        eyebrow: "When to consult",
        heading: "Get evaluated if you have these.",
        bullets: [
          "Gas + weight loss",
          "Gas with diarrhoea > 4 weeks",
          "Gas with abdominal pain or fever",
          "Visible distension that worsens through the day",
          "Family history of celiac or IBD",
        ],
      },
    ],
    faq: [
      { q: "Are probiotics useful for gas?", a: "For some patients with IBS-type symptoms — yes. Choice of strain matters. For SIBO they can sometimes worsen symptoms, so professional guidance helps." },
      { q: "Do I need a colonoscopy for gas alone?", a: "Not for gas alone. Only if there are alarm features — weight loss, blood in stool, family history of bowel cancer, or persistent change in bowel habit." },
      { q: "Is daily activated charcoal safe?", a: "Occasional use is fine; daily use is not recommended as it can interfere with medication absorption." },
      { q: "Can stress cause gas?", a: "Yes — stress directly affects gut motility and sensitivity. Treating anxiety often improves gut symptoms." },
    ],
    related: [
      { label: "IBS Treatment", to: "/symptoms/ibs-symptoms-treatment" },
      { label: "Bloating Treatment", to: "/symptoms/bloating-treatment" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
    ],
    schema: {
      name: "Gas, Bloating and Flatulence",
      signOrSymptom: ["Excess gas", "Abdominal distension", "Belching", "Flatulence", "Discomfort after meals"],
      riskFactor: ["Lactose intolerance", "SIBO", "IBS", "Constipation", "High-FODMAP diet"],
      possibleTreatment: ["Diet modification", "Low-FODMAP plan", "Antibiotics for SIBO", "Prokinetics", "Probiotics"],
    },
  },

  "constipation-specialist-varanasi": {
    shortTitle: "Chronic Constipation",
    title: "Chronic Constipation Treatment in Varanasi",
    subtitle:
      "Hard stools, infrequent motions, straining, the feeling that the bowel doesn't fully empty — chronic constipation is treatable, but quick-fix laxatives often make it worse.",
    seoTitle: "Chronic Constipation Treatment in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Treatment for long-standing constipation, hard stool and incomplete evacuation in Varanasi. Doctor-led, lifestyle-first plan with Dr. Indresh Dixit.",
    sections: [
      {
        id: "what",
        eyebrow: "What counts as constipation",
        heading: "Less than 3 motions a week — and more.",
        body: `Constipation is defined by Rome criteria — fewer than 3 motions per week, hard stool, straining, incomplete evacuation, or the need to manually assist defecation. Many patients have all of these without realising it is a treatable condition.\n\nLong-standing constipation reduces quality of life and raises risk of haemorrhoids, fissures and diverticular disease.`,
      },
      {
        id: "causes",
        eyebrow: "Why it happens",
        heading: "Common reasons we see.",
        bullets: [
          "Low fibre and fluid intake",
          "Sedentary lifestyle",
          "Hypothyroidism, diabetes",
          "Medication side-effect (iron, calcium, opioids, antidepressants)",
          "Pelvic floor dysfunction",
          "Slow-transit colon",
          "Outlet obstruction",
          "Chronic laxative overuse",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "Tests when needed",
        heading: "Look for the cause, not just the symptom.",
        body: `Most patients need only a clinical assessment plus a TSH and basic blood work. Colonoscopy is added when there is blood in stool, weight loss, family history of colon cancer, or new onset over age 45.\n\nFor refractory cases we may do anorectal manometry and a balloon-expulsion test for pelvic-floor dysfunction.`,
      },
      {
        id: "treatment",
        eyebrow: "Treatment plan",
        heading: "Stepwise, sustainable.",
        body: `Step 1: dietary fibre (psyllium), water, regular toilet timing, physical activity. Step 2: osmotic laxatives (PEG-based) — safe long-term. Step 3: secretagogues (lubiprostone, linaclotide) when needed. Step 4: pelvic-floor retraining (biofeedback) when there is dyssynergic defecation.\n\nThe goal is one comfortable motion daily without laxative dependency.`,
        sidenote:
          "Stimulant laxatives (senna, bisacodyl) should NOT be used daily — they cause dependence and dilate the bowel over time.",
      },
      {
        id: "when",
        eyebrow: "When to consult",
        heading: "Red flags that need evaluation.",
        bullets: [
          "Blood in stool",
          "Constipation + unintended weight loss",
          "Sudden change in bowel habit after age 45",
          "Family history of colon cancer",
          "Persistent abdominal pain",
          "Dependence on daily laxatives",
        ],
      },
    ],
    faq: [
      { q: "Is psyllium safe long term?", a: "Yes — psyllium (Isabgol) is a natural soluble fibre, very safe long-term provided enough water is taken alongside it." },
      { q: "Will I need a colonoscopy?", a: "Only if there are alarm features or you are over 45 with new symptoms. For most patients it is not needed." },
      { q: "Can constipation cause piles?", a: "Yes — chronic straining is a major cause of haemorrhoids and anal fissures. Treating constipation often resolves these." },
      { q: "Do probiotics help?", a: "Selected strains help in some patients. They are an adjunct, not a primary treatment." },
    ],
    related: [
      { label: "Blood in Stool", to: "/symptoms/blood-in-stool-treatment" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
      { label: "Abdominal Pain", to: "/symptoms/abdominal-pain-specialist" },
    ],
    schema: {
      name: "Chronic Constipation",
      signOrSymptom: ["Hard stools", "Infrequent motions", "Straining", "Incomplete evacuation"],
      riskFactor: ["Low fibre diet", "Sedentary lifestyle", "Hypothyroidism", "Medications", "Pelvic floor dysfunction"],
      possibleTreatment: ["Dietary fibre", "Osmotic laxatives (PEG)", "Secretagogues", "Pelvic floor biofeedback"],
    },
  },

  "blood-in-stool-treatment": {
    shortTitle: "Blood in Stool",
    title: "Blood in Stool Treatment in Varanasi",
    subtitle:
      "Bright red blood on toilet paper, mixed in the stool, or darker tarry stool — never ignore it. Most causes are minor, but a few are serious and time-sensitive.",
    seoTitle: "Blood in Stool — Treatment in Varanasi by Dr. Indresh Dixit",
    seoDescription:
      "Evaluation and treatment of blood in stool (red blood, melena, occult bleeding) in Varanasi. Same-day colonoscopy where indicated.",
    sections: [
      {
        id: "what",
        eyebrow: "Recognise the pattern",
        heading: "Different colours mean different things.",
        body: `Bright red blood usually comes from the anus, rectum or sigmoid colon — often piles, fissure, or polyps. Maroon blood points to the right colon or small bowel. Black, tarry, foul-smelling stool (melena) means upper GI bleeding from the stomach or duodenum — and that is a medical emergency.\n\nIron and certain medicines also turn stool dark — but never assume; get it checked.`,
      },
      {
        id: "causes",
        eyebrow: "Common causes",
        heading: "From simple to serious.",
        bullets: [
          "Haemorrhoids (piles)",
          "Anal fissure",
          "Colon polyps",
          "Ulcerative colitis or Crohn's disease",
          "Diverticular bleed",
          "Peptic ulcer (causes melena)",
          "Colorectal cancer",
          "Infective colitis",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "How we evaluate",
        heading: "Direct visualisation, fast.",
        body: `Initial steps: history, examination, basic blood work (haemoglobin, iron, CRP). For bright red bleed, we usually plan a colonoscopy in the same week. For melena, an Upper GI Endoscopy is done urgently. Both procedures can both diagnose and stop active bleeding.\n\nIn unexplained chronic bleeding we add capsule endoscopy or CT angiography.`,
        sidenote: "Anyone with rectal bleeding over age 45 — or younger with weight loss / family history — needs a colonoscopy regardless of how 'minor' the bleeding seems.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment options",
        heading: "Treat the source.",
        body: `Piles and fissures often respond to dietary fibre, sitz baths, topical agents and small in-office procedures. Polyps are removed during colonoscopy itself (polypectomy). Active ulcer or diverticular bleeding is controlled endoscopically. Inflammatory bowel disease (UC/Crohn's) gets a structured long-term medical plan. Suspected cancer is staged urgently.`,
      },
      {
        id: "when",
        eyebrow: "When to act",
        heading: "Don't wait if you have these.",
        bullets: [
          "Black, tarry stool (urgent — visit emergency)",
          "Heavy fresh bleeding with dizziness",
          "Bleeding with weight loss or anaemia",
          "Bleeding that recurs over weeks",
          "Family history of colorectal cancer",
        ],
      },
    ],
    faq: [
      { q: "Is bright red blood always piles?", a: "No. Piles are the most common reason but polyps, fissures and rarely cancer can look the same. A proctoscopy or colonoscopy is the only way to be sure." },
      { q: "I take iron — is dark stool normal?", a: "Yes, oral iron can turn stool dark green or black. The texture is different from true melena. When in doubt, test the stool for occult blood." },
      { q: "What is a stool occult blood test?", a: "A simple lab test that detects microscopic blood in stool — useful when you cannot see blood but suspect it." },
      { q: "Can I have colonoscopy on the same day as the consult?", a: "If preparation is feasible — yes. Otherwise it is usually scheduled within 2–3 days." },
    ],
    related: [
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
      { label: "Hemorrhoids (Piles)", to: "/diseases/hemorrhoids-piles-treatment" },
      { label: "Ulcerative Colitis", to: "/diseases/ulcerative-colitis-treatment" },
    ],
    schema: {
      name: "Rectal Bleeding / Blood in Stool",
      alternateName: ["Haematochezia", "Melena"],
      signOrSymptom: ["Bright red blood", "Maroon stool", "Black tarry stool", "Anaemia"],
      riskFactor: ["Haemorrhoids", "Polyps", "IBD", "Diverticular disease", "Peptic ulcer", "Colorectal cancer"],
      possibleTreatment: ["Colonoscopy with polypectomy", "Upper GI endoscopy", "Medical therapy for IBD", "Lifestyle and fibre"],
    },
  },

  "abdominal-pain-specialist": {
    shortTitle: "Abdominal Pain",
    title: "Abdominal Pain Specialist in Varanasi",
    subtitle:
      "Upper, lower, recurrent, post-meal, or sudden — abdominal pain has a long list of causes. The first step is a precise, doctor-led work-up to know exactly which one is yours.",
    seoTitle: "Abdominal Pain Specialist in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Diagnosis and treatment of chronic and acute abdominal pain in Varanasi. Endoscopy, ultrasound, CT and lab work in one place with Dr. Indresh Dixit.",
    sections: [
      {
        id: "what",
        eyebrow: "Where exactly does it hurt?",
        heading: "Location tells us the cause.",
        body: `Upper-right pain often comes from the liver or gallbladder; upper-middle from the stomach, duodenum or pancreas; upper-left from the spleen or pancreas; lower-right from the appendix or ileum; lower-left from the colon. Pain that radiates to the back is classic for pancreas or kidney.\n\nTiming matters as much as location — pain after meals, at night, on an empty stomach, or related to bowels each points in a different direction.`,
      },
      {
        id: "causes",
        eyebrow: "Common causes",
        heading: "What we see most often.",
        bullets: [
          "Peptic ulcer / gastritis",
          "Gallstones / biliary colic",
          "Pancreatitis (acute & chronic)",
          "Irritable bowel syndrome",
          "Diverticular disease",
          "Appendicitis",
          "Kidney stones",
          "Endometriosis (women)",
          "Hernias",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "How we diagnose",
        heading: "Targeted investigation, not a fishing expedition.",
        body: `A focused history and examination usually narrows the cause to two or three options. From there: blood work (LFT, lipase, CBC), abdominal ultrasound, upper GI endoscopy or colonoscopy, and a CT scan when indicated.\n\nWe avoid running every test on every patient — it adds anxiety and rarely helps.`,
        sidenote:
          "Sudden severe pain, fever, vomiting blood, fainting, or pain with rigid abdomen — go to the nearest emergency department immediately.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment",
        heading: "Cause-specific.",
        body: `Ulcer disease — acid suppression plus H. pylori eradication. Gallstone disease — laparoscopic cholecystectomy where indicated. Pancreatitis — admission and supportive care + cause control. IBS — diet, antispasmodics, gut-brain therapies. Each diagnosis has its own protocol; the work begins after a clear answer.`,
      },
      {
        id: "when",
        eyebrow: "When to consult immediately",
        heading: "Red-flag pain features.",
        bullets: [
          "Severe sudden pain",
          "Pain + vomiting blood or coffee-ground material",
          "Pain + rigid abdomen / inability to pass stool or gas",
          "Pain radiating to the back with sweating",
          "Pain + fever > 100°F",
          "Pain in pregnancy",
        ],
      },
    ],
    faq: [
      { q: "When is abdominal pain an emergency?", a: "Sudden severe pain, rigid abdomen, fainting, vomiting blood, or pain with fever should be evaluated immediately." },
      { q: "Do I need an endoscopy and colonoscopy both?", a: "Only if symptoms span upper and lower GI tract or your work-up suggests both. We decide together." },
      { q: "Is recurrent pain always IBS?", a: "No — IBS is a diagnosis of exclusion. We rule out structural causes first." },
      { q: "What about home remedies?", a: "For brief, mild pain after a heavy meal — yes. For pain that lasts >48 hours, recurs, or has any red flag — please get evaluated." },
    ],
    related: [
      { label: "Upper GI Endoscopy", to: "/procedures/upper-gi-endoscopy" },
      { label: "Gallstones / Biliary Disease", to: "/diseases/pancreatitis-treatment" },
      { label: "Peptic Ulcer", to: "/diseases/peptic-ulcer-treatment" },
    ],
    schema: {
      name: "Abdominal Pain",
      signOrSymptom: ["Localised pain", "Diffuse pain", "Post-meal pain", "Night pain", "Radiating pain"],
      riskFactor: ["Peptic ulcer", "Gallstones", "Pancreatitis", "IBS", "Appendicitis"],
      possibleTreatment: ["Endoscopy", "Ultrasound", "CT scan", "Targeted medical therapy", "Surgical referral"],
    },
  },

  "jaundice-treatment": {
    shortTitle: "Jaundice",
    title: "Jaundice Treatment in Varanasi",
    subtitle:
      "Yellow eyes, dark urine, pale stools, fatigue — jaundice is a warning the liver or bile drainage isn't working. The cause matters; the work-up needs to be quick.",
    seoTitle: "Jaundice Treatment in Varanasi — Dr. Indresh Dixit (DM Gastro)",
    seoDescription:
      "Diagnosis and treatment of jaundice (hepatitis, gallstones, bile duct obstruction) in Varanasi. Fast work-up with Dr. Indresh Dixit, DM Gastroenterology.",
    sections: [
      {
        id: "what",
        eyebrow: "What jaundice means",
        heading: "Bilirubin is backing up — but why?",
        body: `Jaundice is the yellowing of skin and eyes from elevated bilirubin. It is a symptom, not a disease. The three broad causes are: too much red-cell breakdown (haemolytic), liver-cell injury (hepatocellular), and blocked bile drainage (obstructive). Each looks slightly different in blood tests and on scans.`,
      },
      {
        id: "causes",
        eyebrow: "Common causes",
        heading: "What we see in Varanasi practice.",
        bullets: [
          "Viral hepatitis (A, E most common)",
          "Hepatitis B and C",
          "Alcoholic liver injury",
          "Drug-induced liver injury",
          "Gallstones blocking the bile duct",
          "Pancreatic head tumour",
          "Cholangiocarcinoma",
          "Autoimmune hepatitis",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "Fast work-up",
        heading: "The first 48 hours matter.",
        body: `Blood tests (LFT, PT/INR, viral hepatitis panel, CBC) plus an abdominal ultrasound are usually enough to separate medical from surgical jaundice. Where bile ducts are dilated, we proceed to MRCP and then ERCP if a stone or stricture is found.\n\nLiver biopsy is rarely needed but reserved for unexplained or autoimmune patterns.`,
        sidenote:
          "Painless jaundice with weight loss — needs immediate evaluation to exclude pancreatic or biliary cancer.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment",
        heading: "Treat the cause, support the liver.",
        body: `Viral hepatitis — supportive care, hydration, monitoring. Hepatitis B/C — antiviral therapy. Bile duct stone — ERCP with stone removal in a single sitting. Pancreatic head mass — biliary stenting via ERCP and onward referral. Drug-induced — withdrawal of offending drug. Autoimmune — immunosuppressive therapy.`,
      },
      {
        id: "when",
        eyebrow: "When to consult",
        heading: "Don't delay if you have these.",
        bullets: [
          "Yellow eyes with fever or abdominal pain",
          "Painless jaundice with weight loss",
          "Confusion, drowsiness, severe vomiting",
          "Bleeding tendency / dark vomit",
          "Pregnancy with jaundice",
        ],
      },
    ],
    faq: [
      { q: "Is jaundice contagious?", a: "Jaundice itself is not. Some causes (Hep A and E) spread through contaminated food/water; Hep B and C through blood/body fluids." },
      { q: "Can jaundice be cured?", a: "Most causes are fully treatable. Viral hepatitis A/E typically resolves in 4–8 weeks. Bile-duct stones are cleared in a single ERCP." },
      { q: "Are home remedies safe?", a: "Hydration and a light, easy-to-digest diet help. Avoid 'liver tonics' and herbal mixes — many actually worsen liver injury." },
      { q: "Do I need admission?", a: "Most patients are managed as outpatients. Admission is needed for severe disease, confusion, vomiting or rapid worsening of liver function." },
    ],
    related: [
      { label: "Hepatitis B Treatment", to: "/diseases/hepatitis-b-treatment" },
      { label: "Hepatitis C Treatment", to: "/diseases/hepatitis-c-treatment" },
      { label: "ERCP", to: "/procedures/ercp" },
    ],
    schema: {
      name: "Jaundice",
      signOrSymptom: ["Yellow eyes", "Dark urine", "Pale stool", "Fatigue", "Itching"],
      riskFactor: ["Hepatitis A/E exposure", "Hepatitis B/C", "Gallstones", "Alcohol", "Drug-induced injury", "Pancreatic cancer"],
      possibleTreatment: ["Antiviral therapy", "ERCP with stone removal", "Biliary stenting", "Supportive care"],
    },
  },

  "bloating-treatment": {
    shortTitle: "Bloating & Fullness",
    title: "Bloating Treatment in Varanasi",
    subtitle:
      "A constant feeling of fullness, visible abdominal distension that worsens through the day, or that 'pregnant-look' after meals — bloating is bothersome but usually fixable.",
    seoTitle: "Bloating Treatment in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Treatment for chronic bloating and abdominal distension in Varanasi. Cause-led management by Dr. Indresh Dixit, DM Gastroenterology (BHU).",
    sections: [
      {
        id: "what",
        eyebrow: "Bloating vs distension",
        heading: "Two different complaints.",
        body: `Bloating is the sensation of fullness. Distension is the visible increase in abdominal girth. Many patients have both, some have only one. The combination changes the work-up.\n\nFunctional bloating (with no underlying disease) is common — but we exclude organic causes first.`,
      },
      {
        id: "causes",
        eyebrow: "Common reasons",
        heading: "Causes ranked by frequency.",
        bullets: [
          "IBS-bloating subtype",
          "SIBO (Small Intestinal Bacterial Overgrowth)",
          "Lactose / fructose intolerance",
          "Constipation",
          "Carbohydrate malabsorption",
          "Gastroparesis (delayed stomach emptying)",
          "Ascites (fluid in abdomen — needs urgent care)",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "Investigations",
        heading: "Pick the right test, not all of them.",
        body: `Most patients are diagnosed after history and examination + a stool/blood panel. Hydrogen-methane breath tests are useful for SIBO and lactose intolerance. Ultrasound rules out ascites. Endoscopy is added for upper-abdominal bloating, especially with reflux or post-meal pain.`,
        sidenote:
          "Distension that does not change with bowel movement, or persistent fluid, needs urgent evaluation to exclude ascites or pelvic mass.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment approach",
        heading: "Diet + targeted medicine.",
        body: `Low-FODMAP trial for 4–6 weeks works for many IBS-bloating patients. SIBO is treated with antibiotics (rifaximin) + prokinetics. Constipation is addressed first if present. Probiotics help in selected cases. Gas-relief drugs are adjuncts, not solutions.`,
      },
      {
        id: "when",
        eyebrow: "When to consult",
        heading: "Don't ignore these signs.",
        bullets: [
          "Bloating + weight loss",
          "Distension that doesn't go down overnight",
          "Bloating with fluid feel (ascites)",
          "Family history of ovarian cancer (women)",
          "Persistent bloating > 3 weeks",
        ],
      },
    ],
    faq: [
      { q: "What is the low-FODMAP diet?", a: "A short, structured elimination of certain fermentable carbohydrates for 4–6 weeks, then re-introduction. It identifies which foods trigger your bloating." },
      { q: "Are gas-relief drugs safe?", a: "Yes, occasional use is fine. They give short-term relief but don't address the cause." },
      { q: "Do probiotics help bloating?", a: "Strain matters. Bifidobacterium-based probiotics help some IBS patients; in SIBO they may worsen symptoms." },
      { q: "Can bloating be a sign of cancer?", a: "Rarely. Persistent bloating with weight loss, change in bowel habit, or fluid in the abdomen needs evaluation to exclude ovarian, pancreatic or colonic causes." },
    ],
    related: [
      { label: "IBS Treatment", to: "/symptoms/ibs-symptoms-treatment" },
      { label: "Gas Problem", to: "/symptoms/gas-problem-treatment-varanasi" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
    ],
    schema: {
      name: "Bloating and Abdominal Distension",
      signOrSymptom: ["Fullness", "Visible distension", "Worse through the day", "Relief on passing gas"],
      riskFactor: ["IBS", "SIBO", "Lactose intolerance", "Constipation", "Gastroparesis"],
      possibleTreatment: ["Low-FODMAP diet", "Rifaximin (SIBO)", "Prokinetics", "Treat constipation"],
    },
  },

  "ibs-symptoms-treatment": {
    shortTitle: "IBS — Irritable Bowel",
    title: "IBS Treatment in Varanasi",
    subtitle:
      "Alternating diarrhoea and constipation, bloating, cramping that improves after a motion — IBS is real, treatable, and very much manageable with the right plan.",
    seoTitle: "IBS (Irritable Bowel Syndrome) Treatment in Varanasi — Dr. Indresh Dixit",
    seoDescription:
      "Long-term management of IBS (Irritable Bowel Syndrome) in Varanasi. Diet, medication and gut-brain therapy with Dr. Indresh Dixit.",
    sections: [
      {
        id: "what",
        eyebrow: "What IBS actually is",
        heading: "A real disorder of gut-brain communication.",
        body: `IBS is a functional bowel disorder — the gut looks normal on tests but works abnormally. The communication between gut nerves and the brain is over-sensitive, motility is altered, and the gut bacteria are imbalanced.\n\nIt is not 'all in your head'. It is real, common, and entirely manageable.`,
      },
      {
        id: "causes",
        eyebrow: "Why IBS happens",
        heading: "Triggers and contributors.",
        bullets: [
          "Post-infectious IBS (after gastroenteritis)",
          "Chronic stress, anxiety, depression",
          "Imbalanced gut microbiota (dysbiosis)",
          "Visceral hypersensitivity",
          "Food intolerances (FODMAPs)",
          "Genetic predisposition",
        ],
      },
      {
        id: "diagnosis",
        eyebrow: "How we diagnose",
        heading: "Confirm IBS, rule out everything else.",
        body: `Rome IV criteria are used — recurrent abdominal pain ≥1 day/week for 3 months, linked to bowel motions. We do basic blood work, thyroid, celiac panel and a stool calprotectin to rule out organic disease. Colonoscopy is added selectively.\n\nLabelling someone with IBS without ruling out organic disease is unsafe.`,
        sidenote:
          "Alarm features that demand colonoscopy in suspected IBS: rectal bleeding, anaemia, weight loss, family history of bowel cancer, onset after 45.",
      },
      {
        id: "treatment",
        eyebrow: "Treatment plan",
        heading: "Four pillars — together.",
        body: `1. Diet — low-FODMAP for 4–6 weeks, then structured re-introduction. 2. Lifestyle — regular meals, sleep, hydration, gentle exercise. 3. Medicine — antispasmodics, peppermint oil, neuromodulators (low-dose) where indicated, rifaximin for the bloat-prominent subtype. 4. Gut-brain — CBT or hypnotherapy for refractory cases.\n\nMost patients get to a 'good baseline' in 8–12 weeks.`,
      },
      {
        id: "when",
        eyebrow: "When to consult",
        heading: "Get evaluated if:",
        bullets: [
          "Symptoms persisting > 3 months",
          "Symptoms disrupting work, sleep or travel",
          "Any blood in stool, fever, weight loss",
          "Family history of IBD or colon cancer",
          "Symptoms started > 45 years",
        ],
      },
    ],
    faq: [
      { q: "Is IBS curable?", a: "It is manageable rather than 'curable'. With the right plan, the majority become symptom-free or near-normal — and stay that way." },
      { q: "Do I really need to do low-FODMAP?", a: "It is the single most effective dietary intervention in IBS — about 70% of patients respond. It is a short, structured trial, not a permanent diet." },
      { q: "Will I need long-term medicine?", a: "Most patients need medicines briefly to break the cycle, then stay on diet and lifestyle. Some need a low-dose neuromodulator long-term." },
      { q: "Are probiotics useful in IBS?", a: "Yes, selected strains help. Generic over-the-counter products often don't. Choice of strain matters." },
    ],
    related: [
      { label: "Bloating Treatment", to: "/symptoms/bloating-treatment" },
      { label: "Crohn's Disease", to: "/diseases/crohns-disease-treatment" },
      { label: "Colonoscopy", to: "/procedures/colonoscopy" },
    ],
    schema: {
      name: "Irritable Bowel Syndrome (IBS)",
      alternateName: ["IBS", "Spastic colon"],
      signOrSymptom: ["Recurrent abdominal pain", "Bloating", "Alternating diarrhoea and constipation", "Relief on motion"],
      riskFactor: ["Post-infectious gut", "Stress / anxiety", "Dysbiosis", "Visceral hypersensitivity"],
      possibleTreatment: ["Low-FODMAP diet", "Antispasmodics", "Neuromodulators", "Rifaximin", "Gut-directed CBT"],
    },
  },
};
